import { Injectable } from '@angular/core';

export interface DraftData {
  id?: number;
  leadId: number;
  userId: number; // Add userId to isolate drafts per user
  type: 'email' | 'sms';
  from?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  content: string;
  fromPhoneNumber?: string;
  toPhoneNumber?: string;
  attachedFiles?: any[];
  staticFiles?: any[];
  replyEmail?: boolean;
  specificEmail?: string;
  replySubject?: string;
  createEmail?: boolean;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class DraftStorageService {
  private dbName = 'EmailSmsDB';
  private storeName = 'drafts';
  private db: IDBDatabase | null = null;
  private dbVersion = 3; // Increment version to trigger upgrade
  private cleanupInterval: any;
  private readonly DRAFT_LIFETIME_MINUTES = 60; // 1 hour
  private readonly CACHE_NAME = 'draft-files-cache';

  constructor() {
    this.initDB().then(() => {
      // Clean old drafts on startup
      this.cleanOldDrafts();
      
      // Set up periodic cleanup every 5 minutes
      this.cleanupInterval = setInterval(() => {
        this.cleanOldDrafts();
      }, 5 * 60 * 1000);
    });
  }

  ngOnDestroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        
        if (db.objectStoreNames.contains(this.storeName)) {
          db.deleteObjectStore(this.storeName);
        }
        
        const objectStore = db.createObjectStore(this.storeName, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        
        objectStore.createIndex('leadId', 'leadId', { unique: false });
        objectStore.createIndex('userId', 'userId', { unique: false });
        objectStore.createIndex('type', 'type', { unique: false });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        // Composite index for efficient querying by user + lead + type
        objectStore.createIndex('user_lead_type', ['userId', 'leadId', 'type'], { unique: false });
      };
    });
  }

  private async ensureDB(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
  }

  /**
   * Generate a unique cache key including user ID, lead ID and type
   */
  private generateCacheKey(userId: number, leadId: number, type: 'email' | 'sms', fileIndex: number): string {
    // Include userId in the cache key for complete isolation
    return `draft-user${userId}-lead${leadId}-${type}-file${fileIndex}-${Date.now()}`;
  }

  /**
   * Store files in Cache API with proper user and type separation
   */
  private async cacheFiles(userId: number, leadId: number, type: 'email' | 'sms', files: any[]): Promise<any[]> {
    if (!files || files.length === 0) return [];
    
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const cachedFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (file instanceof File) {
          // Create a unique cache key with user, lead and type
          const cacheKey = this.generateCacheKey(userId, leadId, type, i);
          const cacheUrl = new Request(cacheKey);
          
          // Store the file in cache
          await cache.put(cacheUrl, new Response(file));
          
          // Store reference with metadata
          cachedFiles.push({
            cacheKey,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            isFile: true,
            userId,
            leadId,
            draftType: type
          });
        } else {
          // Static files remain as-is
          cachedFiles.push(file);
        }
      }
      
      return cachedFiles;
    } catch (error) {
      console.error('Error caching files:', error);
      return [];
    }
  }

  private async retrieveCachedFiles(fileReferences: any[]): Promise<any[]> {
    if (!fileReferences || fileReferences.length === 0) return [];
    
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const files = [];
      
      for (const ref of fileReferences) {
        if (ref.isFile && ref.cacheKey) {
          try {
            const cacheUrl = new Request(ref.cacheKey);
            const response = await cache.match(cacheUrl);
            
            if (response) {
              const blob = await response.blob();
              
              // Verify blob is valid before creating file
              if (blob && blob.size > 0) {
                const file = new File([blob], ref.name, { 
                  type: ref.type,
                  lastModified: ref.lastModified 
                });
                
                // Add URL and cache key for preview and removal
                const fileWithUrl = Object.assign(file, {
                  url: URL.createObjectURL(file),
                  cacheKey: ref.cacheKey,
                  userId: ref.userId,
                  leadId: ref.leadId,
                  draftType: ref.draftType
                });
                
                files.push(fileWithUrl);
              }
              // If blob is invalid, skip the file (don't add placeholder)
            }
            // If no response in cache, skip the file entirely
          } catch (error) {
            console.error(`Error retrieving cached file ${ref.name}:`, error);
            // Skip this file, don't add to results
          }
        } else {
          // Static files remain as-is
          files.push(ref);
        }
      }
      
      return files;
    } catch (error) {
      console.error('Error retrieving cached files:', error);
      return [];
    }
  }

  /**
   * Remove a specific file from cache
   */
  async removeCachedFile(cacheKey: string): Promise<void> {
    if (!cacheKey) return;
    
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const cacheUrl = new Request(cacheKey);
      await cache.delete(cacheUrl);
    } catch (error) {
      console.error(`Error removing cached file ${cacheKey}:`, error);
    }
  }

  /**
   * Clean up cached files for a draft
   */
  private async cleanupCachedFiles(fileReferences: any[]): Promise<void> {
    if (!fileReferences || fileReferences.length === 0) return;
    
    try {
      const cache = await caches.open(this.CACHE_NAME);
      
      for (const ref of fileReferences) {
        if (ref.isFile && ref.cacheKey) {
          try {
            const cacheUrl = new Request(ref.cacheKey);
            await cache.delete(cacheUrl);
          } catch (error) {
            console.error(`Error deleting cached file ${ref.cacheKey}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error cleaning up cached files:', error);
    }
  }

  private removeSignatureFromContent(content: string): string {
    if (!content || typeof content !== 'string') {
      return content;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const signatureElements = tempDiv.querySelectorAll('div.email-signature-content[data-signature="true"]');
    signatureElements.forEach(element => {
      element.remove();
    });

    const signatureById = tempDiv.querySelector('#signature-wrapper');
    if (signatureById) {
      signatureById.remove();
    }

    return tempDiv.innerHTML;
  }

  async saveDraft(draft: DraftData, userId: number): Promise<number> {
    await this.ensureDB();
  
    draft.leadId = Number(draft.leadId);
    draft.userId = Number(userId); // Ensure userId is set
    
    // Filter out any files that might have been marked for removal
    if (draft.attachedFiles) {
      draft.attachedFiles = draft.attachedFiles.filter(file => 
        !file.isPlaceholder && !file.error
      );
    }
    
    // Get existing draft for this user, lead and type
    const existingDraft = await this.getDraft(draft.leadId, draft.type, userId);
    
    let draftToSave: any = {
      id: existingDraft?.id,
      leadId: draft.leadId,
      userId: draft.userId,
      type: draft.type,
      content: draft.content || '',
      timestamp: Date.now()
    };

    // Clean up old cached files if they exist
    if (existingDraft) {
      const oldAttachedFiles = existingDraft.attachedFiles || [];
      const oldStaticFiles = existingDraft.staticFiles || [];
      
      // Find files that were removed
      const removedAttached = oldAttachedFiles.filter(oldFile => 
        oldFile.isFile && !draft.attachedFiles?.some(newFile => 
          newFile.name === oldFile.name && newFile.size === oldFile.size
        )
      );
      
      const removedStatic = oldStaticFiles.filter(oldFile => 
        oldFile.isFile && !draft.staticFiles?.some(newFile => 
          newFile.name === oldFile.name && newFile.size === oldFile.size
        )
      );
      
      // Clean up removed files
      await this.cleanupCachedFiles([...removedAttached, ...removedStatic]);
    }

    if (draft.type === 'email') {
      draftToSave.content = this.removeSignatureFromContent(draft.content || '');
      draftToSave.subject = draft.subject || '';
      
      // Cache files with user and type separation
      draftToSave.attachedFiles = await this.cacheFiles(userId, draft.leadId, 'email', draft.attachedFiles || []);
      draftToSave.staticFiles = await this.cacheFiles(userId, draft.leadId, 'email', draft.staticFiles || []);
    } else {
      // For SMS, save all fields
      draftToSave.from = draft.from;
      draftToSave.to = draft.to;
      draftToSave.fromPhoneNumber = draft.fromPhoneNumber;
      draftToSave.toPhoneNumber = draft.toPhoneNumber;
      
      // Cache files with user and type separation
      draftToSave.attachedFiles = await this.cacheFiles(userId, draft.leadId, 'sms', draft.attachedFiles || []);
      draftToSave.staticFiles = await this.cacheFiles(userId, draft.leadId, 'sms', draft.staticFiles || []);
      
      draftToSave.replyEmail = draft.replyEmail;
      draftToSave.specificEmail = draft.specificEmail;
      draftToSave.replySubject = draft.replySubject;
      draftToSave.createEmail = draft.createEmail;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      let request: IDBRequest;
      
      if (existingDraft && existingDraft.id) {
        draftToSave.id = existingDraft.id;
        request = store.put(draftToSave);
      } else {
        delete draftToSave.id;
        request = store.add(draftToSave);
      }
      
      request.onsuccess = () => {
        resolve(request.result as number);
      };
      
      request.onerror = () => {
        console.error('Failed to save draft:', request.error);
        reject(request.error);
      };
    });
  }

  async getDraft(leadId: number, type: 'email' | 'sms', userId: number): Promise<DraftData | null> {
    await this.ensureDB();
    
    leadId = Number(leadId);
    userId = Number(userId);
    
    return new Promise(async (resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const index = store.index('user_lead_type');
        
        // Query using composite index for user + lead + type
        const request = index.openCursor([userId, leadId, type]);
        
        request.onsuccess = async () => {
          const cursor = request.result;
          if (cursor) {
            const draftAge = Date.now() - cursor.value.timestamp;
            const maxAge = this.DRAFT_LIFETIME_MINUTES * 60 * 1000;
            
            if (draftAge < maxAge) {
              const draft = cursor.value;
              
              // Retrieve cached files
              if (draft.attachedFiles || draft.staticFiles) {
                draft.attachedFiles = await this.retrieveCachedFiles(draft.attachedFiles || []);
                draft.staticFiles = await this.retrieveCachedFiles(draft.staticFiles || []);
              }
              
              resolve(draft);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get draft:', request.error);
          resolve(null);
        };
      } catch (error) {
        console.error('Error getting draft:', error);
        resolve(null);
      }
    });
  }

  async deleteDraft(id: number): Promise<void> {
    await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      // First transaction to get the draft data
      const getTransaction = this.db!.transaction([this.storeName], 'readonly');
      const getStore = getTransaction.objectStore(this.storeName);
      const getRequest = getStore.get(id);
      
      getRequest.onsuccess = async () => {
        const draft = getRequest.result;
        
        if (draft) {
          // Clean up cached files BEFORE starting delete transaction
          await this.cleanupCachedFiles(draft.attachedFiles || []);
          await this.cleanupCachedFiles(draft.staticFiles || []);
          
          // Now create a new transaction for deletion
          const deleteTransaction = this.db!.transaction([this.storeName], 'readwrite');
          const deleteStore = deleteTransaction.objectStore(this.storeName);
          const deleteRequest = deleteStore.delete(id);
          
          deleteRequest.onsuccess = () => {
            resolve();
          };
          
          deleteRequest.onerror = () => {
            console.error('Failed to delete draft:', deleteRequest.error);
            resolve(); // Resolve anyway to prevent blocking
          };
        } else {
          resolve(); // No draft found
        }
      };
      
      getRequest.onerror = () => {
        console.error('Failed to get draft for deletion:', getRequest.error);
        resolve();
      };
    });
  }

  async deleteDraftByLeadAndType(leadId: number, type: 'email' | 'sms', userId: number): Promise<void> {
    const draft = await this.getDraft(leadId, type, userId);
    if (draft && draft.id) {
      await this.deleteDraft(draft.id);
    }
  }

  async cleanOldDrafts(): Promise<void> {
    await this.ensureDB();
    
    const cutoffTime = Date.now() - (this.DRAFT_LIFETIME_MINUTES * 60 * 1000);
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      
      // First, collect all old drafts
      const oldDrafts: any[] = [];
      const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          oldDrafts.push(cursor.value);
          cursor.continue();
        } else {
          // Now delete them one by one
          this.deleteOldDraftsSequentially(oldDrafts).then(() => resolve());
        }
      };
      
      request.onerror = () => {
        console.error('Failed to query old drafts:', request.error);
        resolve();
      };
    });
  }

  private async deleteOldDraftsSequentially(drafts: any[]): Promise<void> {
    for (const draft of drafts) {
      if (draft.id) {
        await this.deleteDraft(draft.id);
      }
    }
  }

  async getAllDraftsForLead(leadId: number, userId: number): Promise<DraftData[]> {
    await this.ensureDB();
    
    leadId = Number(leadId);
    userId = Number(userId);
    
    return new Promise(async (resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const index = store.index('user_lead_type');
        
        // Get all drafts for this user and lead (both email and sms)
        const drafts: DraftData[] = [];
        const maxAge = this.DRAFT_LIFETIME_MINUTES * 60 * 1000;
        
        // Query for email drafts
        const emailRequest = index.openCursor([userId, leadId, 'email']);
        
        emailRequest.onsuccess = async () => {
          const cursor = emailRequest.result;
          if (cursor) {
            const draftAge = Date.now() - cursor.value.timestamp;
            if (draftAge < maxAge) {
              const draft = cursor.value;
              if (draft.attachedFiles || draft.staticFiles) {
                draft.attachedFiles = await this.retrieveCachedFiles(draft.attachedFiles || []);
                draft.staticFiles = await this.retrieveCachedFiles(draft.staticFiles || []);
              }
              drafts.push(draft);
            }
            cursor.continue();
          } else {
            // Now query for SMS drafts
            const smsRequest = index.openCursor([userId, leadId, 'sms']);
            
            smsRequest.onsuccess = async () => {
              const cursor = smsRequest.result;
              if (cursor) {
                const draftAge = Date.now() - cursor.value.timestamp;
                if (draftAge < maxAge) {
                  const draft = cursor.value;
                  if (draft.attachedFiles || draft.staticFiles) {
                    draft.attachedFiles = await this.retrieveCachedFiles(draft.attachedFiles || []);
                    draft.staticFiles = await this.retrieveCachedFiles(draft.staticFiles || []);
                  }
                  drafts.push(draft);
                }
                cursor.continue();
              } else {
                resolve(drafts);
              }
            };
            
            smsRequest.onerror = () => {
              console.error('Failed to get SMS drafts:', smsRequest.error);
              resolve(drafts); // Return what we have
            };
          }
        };
        
        emailRequest.onerror = () => {
          console.error('Failed to get email drafts:', emailRequest.error);
          resolve([]);
        };
      } catch (error) {
        console.error('Error getting all drafts:', error);
        resolve([]);
      }
    });
  }

  async getDraftById(id: number): Promise<DraftData | null> {
    await this.ensureDB();
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
      
      request.onerror = () => {
        resolve(null);
      };
    });
  }

  async deleteAllDraftsForLead(leadId: number, userId: number): Promise<void> {
    const drafts = await this.getAllDraftsForLead(leadId, userId);
    
    for (const draft of drafts) {
      if (draft.id) {
        await this.deleteDraft(draft.id);
      }
    }
  }
  
  async forceCleanup(): Promise<void> {
    await this.cleanOldDrafts();
  }

  /**
   * Clear all drafts for a specific user (useful on logout)
   */
  async clearUserDrafts(userId: number): Promise<void> {
    await this.ensureDB();
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      
      // Get all drafts for this user
      const userDrafts: any[] = [];
      const request = index.openCursor(userId);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          userDrafts.push(cursor.value);
          cursor.continue();
        } else {
          // Delete all user drafts
          this.deleteOldDraftsSequentially(userDrafts).then(() => resolve());
        }
      };
      
      request.onerror = () => {
        console.error('Failed to query user drafts:', request.error);
        resolve();
      };
    });
  }
}